import {useCallback, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import useWindowSize from '@/hooks/WindowSize/WindowSize';

import {DEFAULT_THRESHOLD} from './models/constants';
import {StepDef} from './models/types';
import {useTrackers} from './trackers';

type TermsAndConditionsUseActionsProps = {
  open: boolean;
  onClose: () => void;
  steps: StepDef[];
  scrollThreshold?: number;
};

export const useActions = ({
  open,
  onClose,

  steps,
  scrollThreshold = DEFAULT_THRESHOLD,
}: TermsAndConditionsUseActionsProps) => {
  const [indexActive, setIndexActive] = useState(0);
  const [seen, setSeen] = useState<boolean[]>(() => steps.map(() => false));
  const [canContinue, setCanContinue] = useState(false);
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(true);

  const total = steps.length;
  const current = indexActive + 1;
  const scrollableContentRef = useRef<HTMLDivElement>(null);

  const {isMobile} = useWindowSize();
  const {t} = useTranslation();

  const {trackTermsAndConditions} = useTrackers();

  useEffect(() => {
    if (!open) return;
    setIndexActive(0);
    setSeen(steps.map(() => false));
    setCanContinue(false);
  }, [open, steps, scrollableContentRef]);

  useEffect(() => {
    scrollableContentRef.current?.scrollTo({top: 0});
  }, [indexActive, scrollableContentRef]);

  useEffect(() => {
    setCanContinue(seen[indexActive] ?? false);
  }, [indexActive, seen]);

  const markSeen = useCallback(() => {
    setSeen(prev => prev.map((v, i) => (i === indexActive ? true : v)));
    setCanContinue(true);
  }, [indexActive]);

  const handleScroll = useCallback(() => {
    const el = scrollableContentRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const percent = max <= 0 ? 100 : (el.scrollTop / max) * 100;
    setIsScrollButtonVisible(percent < DEFAULT_THRESHOLD);

    if (percent >= scrollThreshold && !seen[indexActive]) markSeen();
  }, [indexActive, seen, scrollThreshold, markSeen]);

  const handleNext = useCallback(() => {
    const step = steps[indexActive];

    if (step?.nextEventName) {
      trackTermsAndConditions(step.nextEventName);
    }

    if (!seen[indexActive]) {
      setSeen(prev => prev.map((v, i) => (i === indexActive ? true : v)));
    }
    if (indexActive === total - 1) onClose();
    else setIndexActive(i => i + 1);
  }, [steps, indexActive, seen, total, onClose, trackTermsAndConditions]);

  const handleBack = useCallback(() => {
    const step = steps[indexActive];

    if (step?.backEventName) {
      trackTermsAndConditions(step.backEventName);
    }
    setIndexActive(prev => Math.max(0, prev - 1));
  }, [indexActive, steps, trackTermsAndConditions]);

  const scrollToBottom = useCallback(() => {
    const el = scrollableContentRef.current;
    if (!el) return;
    el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
  }, [scrollableContentRef]);

  return {
    indexActive,
    total,
    current,
    canContinue,
    seen,
    handleScroll,
    handleNext,
    handleBack,
    scrollToBottom,
    scrollableContentRef,
    isMobile,
    t,
    isScrollButtonVisible,
  };
};
